import { AccountBalanceDTO } from 'src/app/models/account/account-balance-dto';
import { Currency } from 'src/app/models/enums/currency';
import { KpiExtra, KpiTone } from 'src/app/shared/components/kpi-card/kpi-card.component';
import { CurrencyAmountPipe } from 'src/app/shared/pipes/currency-amount.pipe';
import { SubTypeShortPipe } from 'src/app/shared/pipes/sub-type-short.pipe';

/**
 * Pano bakiye kartları (Şube Bakiyesi ve Genel Bakiye sekmeleri) için ortak yardımcılar.
 * Kart app-kpi-card ile çizilir: ana değer toplam bakiye, kasa ve müşteri bakiyesi ek değer
 * (müşteri bakiyesi artı yeşil, eksi kırmızı — eski kartlardaki gibi).
 */
export interface BalanceKpiCard {
  currency: Currency;
  label: string;
  /** Toplam bakiye. */
  value: string;
  icon: string;
  tone: KpiTone;
  extras: KpiExtra[];
}

const amountPipe = new CurrencyAmountPipe();
const subTypePipe = new SubTypeShortPipe();

/** Kasa, müşteri ya da toplam bakiyesinden biri sıfır değilse kart gösterilir. */
export function hasBalance(item?: AccountBalanceDTO | null): boolean {
  return !!item && ((item.balance || 0) !== 0 || (item.customersBalance || 0) !== 0 || (item.finalBalance || 0) !== 0);
}

export function balanceKpiCard(item: AccountBalanceDTO, options: { showSubType?: boolean } = {}): BalanceKpiCard {
  // Sarrafiye tutarları altın gibi (3 ondalık) yazılır — eski Şube Bakiyesi kartlarıyla aynı.
  const formatCurrency = item.currency === Currency.Accessories || item.currency === Currency.OtherAccessories
    ? Currency.Gold
    : item.currency;
  const amount = (value?: number) => amountPipe.transform(value || 0, formatCurrency, item.subType) || '0';
  const subType = options.showSubType && item.subType != null ? subTypePipe.transform(item.subType, item.currency) : '';

  return {
    currency: item.currency,
    label: currencyTitle(item.currency) + (subType ? ` (${subType})` : ''),
    value: amount(item.finalBalance),
    icon: currencyIcon(item.currency),
    tone: currencyTone(item.currency),
    extras: [
      { label: 'Kasa', value: amount(item.balance) },
      {
        label: 'Müşteri',
        value: amount(item.customersBalance),
        tone: (item.customersBalance || 0) >= 0 ? 'success' : 'danger',
      },
    ],
  };
}

export function currencyTitle(currency?: Currency): string {
  switch (currency) {
    case Currency.Tl: return 'Türk Lirası';
    case Currency.Usd: return 'Dolar';
    case Currency.Euro: return 'Euro';
    case Currency.Gold: return 'Altın';
    case Currency.GoldNew: return 'Vitrin';
    case Currency.GoldIgnot: return 'Külçe';
    case Currency.Accessories: return 'Sarrafiye';
    case Currency.OtherAccessories: return 'Sarrafiye (Diğer)';
    case Currency.Silver: return 'Gümüş';
    case Currency.Other: return 'Diğer';
    default: return currency != null ? Currency[currency] || '' : '';
  }
}

/** Kart rengi — eski kart başlıklarındaki renklerle aynı: TL kırmızı, USD yeşil, EUR mavi, altın türleri sarı. */
export function currencyTone(currency?: Currency): KpiTone {
  switch (currency) {
    case Currency.Tl: return 'danger';
    case Currency.Usd: return 'success';
    case Currency.Euro: return 'info';
    // Eski karttaki gri (secondary) KPI kartında yok; gümüş için soğuk ton.
    case Currency.Silver: return 'info';
    default: return 'warning';
  }
}

export function currencyIcon(currency?: Currency): string {
  switch (currency) {
    case Currency.Tl: return 'bx bx-lira';
    case Currency.Usd: return 'bx bx-dollar';
    case Currency.Euro: return 'bx bx-euro';
    case Currency.Accessories:
    case Currency.OtherAccessories: return 'bx bx-coin-stack';
    case Currency.Other: return 'bx bx-wallet';
    default: return 'mdi mdi-gold';
  }
}
